import customConfig from "../config";

export const ROLE_SYSTEM_EWARRANTY_NOTIFICATION = "SYSTEM_EWARRANTY_NOTIFICATION";

export const IN_APP_NOTIFICATION_MODULE_NAME = "In-App Notifications";

export const IN_APP_NOTIFICATION_ROUTES = {
  list: "/inapp-notifications",
  create: "/inapp-notifications/create",
  edit: (notificationId) =>
    `/inapp-notifications/edit/${encodeURIComponent(notificationId)}`,
  view: (notificationId) =>
    `/inapp-notifications/view/${encodeURIComponent(notificationId)}`,
};

export const buildNotificationListState = ({
  page,
  rowsPerPage,
  orderBy,
  order,
  targetType,
}) => ({
  page,
  rowsPerPage,
  orderBy,
  order,
  targetType,
});

export const buildNotificationListRouteState = ({
  targetType,
  listState,
  refresh,
  feedback,
  targetUserCount,
  restoreListPagination,
} = {}) => ({
  ...(targetType ? { notificationTargetType: targetType } : {}),
  ...(listState ? { listState } : {}),
  ...(refresh ? { refresh: true } : {}),
  ...(feedback ? { feedback } : {}),
  ...(targetUserCount != null ? { targetUserCount } : {}),
  ...(restoreListPagination ? { restoreListPagination: true } : {}),
});

const LIST_TAB_DEFAULTS = {
  targetType: "broadcast",
  page: 0,
  rowsPerPage: 10,
  orderBy: null,
  order: "asc",
};

let pendingListPagination = null;

export const setPendingListPagination = (listState) => {
  pendingListPagination = listState;
};

export const clearPendingListPagination = () => {
  pendingListPagination = null;
};

export const isBrowserPageReload = () => {
  const navEntry = performance.getEntriesByType("navigation")[0];
  return navEntry?.type === "reload";
};

const withListTabDefaults = (partial, fallbackTargetType = "broadcast") => ({
  targetType: partial?.targetType || fallbackTargetType,
  page: typeof partial?.page === "number" ? partial.page : LIST_TAB_DEFAULTS.page,
  rowsPerPage:
    typeof partial?.rowsPerPage === "number"
      ? partial.rowsPerPage
      : LIST_TAB_DEFAULTS.rowsPerPage,
  orderBy: partial?.orderBy !== undefined ? partial.orderBy : LIST_TAB_DEFAULTS.orderBy,
  order: partial?.order || LIST_TAB_DEFAULTS.order,
});

export const resolveNotificationListStateFromLocation = (location) => {
  const navigationState = location?.state || {};

  if (navigationState.refresh) {
    clearPendingListPagination();
    return withListTabDefaults({ targetType: navigationState.notificationTargetType });
  }

  if (navigationState.restoreListPagination) {
    const pending = pendingListPagination;
    pendingListPagination = null;

    const restorable =
      pending ||
      (!isBrowserPageReload() ? navigationState.listState : null);

    if (restorable) {
      return withListTabDefaults(
        restorable,
        navigationState.notificationTargetType || restorable.targetType,
      );
    }

    if (isBrowserPageReload()) {
      clearPendingListPagination();
      return null;
    }
  }

  if (isBrowserPageReload()) {
    clearPendingListPagination();
    if (navigationState.notificationTargetType) {
      return withListTabDefaults({ targetType: navigationState.notificationTargetType });
    }
    return null;
  }

  if (navigationState.notificationTargetType) {
    return withListTabDefaults({ targetType: navigationState.notificationTargetType });
  }

  return null;
};

export const DEFAULT_NOTIFICATION_LIST_TAB_UI = LIST_TAB_DEFAULTS;

export const TARGET_TYPE_OPTIONS = [
  { value: "broadcast", label: "Broadcast" },
  { value: "user-specific", label: "User-Specific" },
];

export const DISPLAY_TYPE_OPTIONS = [
  { value: "Card", label: "Card" },
  { value: "Carousel", label: "Carousel" },
];

export const ASSET_SOURCE_OPTIONS = [
  { value: "existing", label: "Use Existing Asset" },
  { value: "create", label: "Create New Asset" },
];

export const ASSIGNMENT_TARGET_OPTIONS = [
  { value: "userIds", label: "User IDs" },
  { value: "mobileNo", label: "Mobile Numbers" },
];

export const ASSIGNMENT_PERMISSION_ERROR_MESSAGE =
  "Insufficient permission to assign notifications.\n\nRequired:\n• L3 User\nOR\n• ECareWiz Admin";

export const ASSIGNMENT_ACCESS_DENIED_MESSAGE =
  "Only L3 users or ECareWiz Admin users can configure user-specific notifications.";

export const USER_SPECIFIC_NOTIFICATION_EMPTY_MESSAGE =
  "No user-specific notifications available. Create a User-Specific Notification first.";

export const DELIVERY_RADIO_COLUMN_WIDTHS = ["100px", "124px"];

export const PLATFORM_OPTIONS = [
  { value: "ios", label: "iOS" },
  { value: "an", label: "Android" },
];

export const IN_APP_NOTIFICATION_CTA_TARGET_PREFIX = "miraie://";

export const IN_APP_NOTIFICATION_API_BASE_PATH =
  "/simplifi/v1/notification/inapp";

export const IN_APP_NOTIFICATION_VALIDATIONS = {
  title: { maxLength: 200, required: true },
  body: { maxLength: 2000, required: true },
  screenDesc: { maxLength: 2000, required: true },
  ctaId: { maxLength: 20, required: false },
  ctaLabel: { maxLength: 20, required: false },
  ctaTarget: { maxLength: 200, required: false },
  assetId: { maxLength: 64, required: false },
  assetTag: { maxLength: 120, required: false },
  maxCtasPerScreen: 2,
  minCtasPerScreen: 0,
  minScreensPerCarousel: 1,
  expiryMaxDaysFromToday: 30,
  configMaxPerDay: { min: 1, max: 3 },
  configMaxPerWeek: { min: 1, max: 7 },
  listMaxPageSize: 100,
};

export const sectionCardSx = {
  p: { xs: 1.5, md: 2 },
  mt: { xs: 1, md: 1.5 },
  boxShadow: 2,
  borderRadius: 1,
};

export const inAppPageTitleSx = {
  mb: { xs: 1.5, md: 2 },
  fontWeight: 700,
  fontSize: { xs: "1.125rem", sm: "1.25rem", md: "1.5rem" },
};

export const inAppTabPanelSx = {
  mt: { xs: 1.5, md: 2 },
  width: "100%",
  minWidth: 0,
};

export const inAppToolbarRowSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: { xs: "stretch", sm: "flex-end" },
  flexDirection: { xs: "column", sm: "row" },
  flexWrap: "wrap",
  gap: { xs: 1, sm: 1.5 },
};

export const inAppPrimaryActionSx = {
  alignSelf: { xs: "stretch", sm: "center" },
  width: { xs: "100%", sm: "auto" },
};

export const inAppListTableShellSx = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minWidth: 0,
  minHeight: { xs: 280, sm: 360, md: 420 },
  height: {
    xs: "min(65vh, 560px)",
    sm: "min(58vh, 620px)",
    md: "calc(100vh - 320px)",
  },
  maxHeight: { xs: "70vh", md: "none" },
  overflow: "hidden",
};

export const inAppFooterActionsSx = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: { xs: "stretch", sm: "flex-end" },
  flexDirection: { xs: "column-reverse", sm: "row" },
  gap: 1,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  "& > .MuiButton-root": {
    width: { xs: "100%", sm: "auto" },
  },
};

export const inAppSectionIconRowSx = {
  display: "flex",
  alignItems: "flex-start",
  flexDirection: { xs: "column", sm: "row" },
  gap: { xs: 1, sm: 1.5 },
  width: "100%",
  minWidth: 0,
  "& > .MuiSvgIcon-root:first-of-type": {
    display: { xs: "none", sm: "block" },
  },
};

export const inAppCardHeaderRowSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: { xs: "stretch", sm: "flex-start" },
  flexDirection: { xs: "column", sm: "row" },
  gap: { xs: 1.5, sm: 2 },
  flexWrap: "wrap",
};

export const inAppRadioGroupResponsiveSx = {
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "flex-start", sm: "center" },
  rowGap: { xs: 0.5, sm: 0 },
  width: { xs: "100%", sm: "fit-content" },
};

export const formControlSx = (theme) => ({
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  [theme.breakpoints.up("md")]: {
    maxWidth: "100%",
  },
});

export const compactInputSx = {
  width: "100%",
  minWidth: 0,
  maxWidth: { xs: "100%", md: 320 },
};

export const pageFormInputSx = {
  width: "100%",
  minWidth: 0,
  maxWidth: "100%",
};

export const pageAssetIdControlRowSx = {
  width: { xs: "100%", md: "50%" },
  minWidth: 0,
  maxWidth: { xs: "100%", md: "50%" },
};

export const pageFormShortInputSx = {
  width: "100%",
  minWidth: 0,
  maxWidth: { xs: "100%", md: 420, lg: 480 },
};

export const pageFormMediumInputSx = {
  width: "100%",
  minWidth: 0,
  maxWidth: { xs: "100%", md: 280 },
};

export const pageFormFieldsGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    md: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(3, minmax(0, 1fr))",
  },
  gap: { xs: 1, md: 1.25 },
  width: "100%",
  minWidth: 0,
  alignItems: "start",
};

export const pageAssetOnlyGridSx = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateAreas: `"assetLabel" "assetControls"`,
  rowGap: 0.75,
  alignItems: "start",
  width: "100%",
  minWidth: 0,
};

export const pageCardConfigGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
  gridTemplateAreas: {
    xs: `"assetLabel"
         "assetControls"
         "ctaLabel"
         "ctaControls"`,
    md: `"assetLabel ctaLabel"
         "assetControls ctaControls"`,
  },
  columnGap: 1.25,
  rowGap: 0.75,
  alignItems: "start",
  width: "100%",
  minWidth: 0,
};

export const pageSubsectionLabelSx = {
  width: "100%",
  minWidth: 0,
  alignSelf: { md: "end" },
};

export const pageSubsectionInputRowSx = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  gap: 0.75,
  width: "100%",
  minWidth: 0,
  minHeight: { xs: "auto", sm: 40 },
};

export const pageFormInlineActionSx = {
  flexShrink: 0,
  whiteSpace: "nowrap",
  height: 40,
  minHeight: 40,
  alignSelf: { xs: "stretch", sm: "center" },
  width: { xs: "100%", sm: "auto" },
};

export const pageSubsectionHeaderSx = pageSubsectionLabelSx;

export const pageFormCtaGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "minmax(0, 1fr) minmax(0, 1.4fr) 36px",
  },
  gap: { xs: 0.75, sm: 1 },
  alignItems: "center",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
};

export const PAGE_FORM_LABEL_WIDTH = 160;

export const pageFormGridColumns = {
  xs: "1fr",
  md: `${PAGE_FORM_LABEL_WIDTH}px minmax(0, 1fr)`,
};

export const pageFormPairGridColumns = {
  xs: "1fr",
  md: `${PAGE_FORM_LABEL_WIDTH}px minmax(0, 1fr) minmax(0, 1fr)`,
};

export const pageFormExpiryInnerGridSx = {
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  gap: 0.75,
  alignItems: "center",
  minWidth: 0,
  width: "100%",
};

export const pageFormGridSx = {
  display: "grid",
  gridTemplateColumns: pageFormGridColumns,
  gap: { xs: 0.75, md: 1.5 },
  width: "100%",
  alignItems: "center",
  overflow: "visible",
};

export const pageFormPairGridSx = {
  display: "grid",
  gridTemplateColumns: pageFormPairGridColumns,
  gap: { xs: 0.75, md: 1.5 },
  width: "100%",
  alignItems: "center",
  overflow: "visible",
};

export const pageFormLabelSx = {
  fontWeight: 600,
  color: "text.primary",
  lineHeight: 1.4,
  wordBreak: "break-word",
  textAlign: "left",
};

export const borderedSectionSx = {
  mx: { xs: -1, md: -1.5 },
  px: { xs: 1, md: 1.5 },
};

export const compactFormGridColumns = {
  xs: "1fr",
  md: "minmax(132px, 160px) minmax(0, 320px)",
};

export const compactFormGridSx = (compact = true) => ({
  display: "grid",
  gridTemplateColumns: compact
    ? compactFormGridColumns
    : { xs: "1fr", md: "minmax(140px, 200px) minmax(0, 1fr)" },
  gap: { xs: 0.5, md: compact ? 1.25 : 2 },
  width: "100%",
  overflow: "visible",
});

export const EMPTY_ASSET = {
  asset: "",
  assetTag: "",
  resolutions: [],
  tags: [],
};
export const EMPTY_CTA = { id: "", label: "", target: "" };

export const createDefaultCtaList = () => [];

export const createEmptyNotificationForm = (targetType = "broadcast") => ({
  id: "",
  title: "",
  body: "",
  targetType,
  type: "Card",
  platform: [],
  expiryDate: null,
  config: { maxPerDay: "", maxPerWeek: "" },
  assets: { ...EMPTY_ASSET },
  cta: createDefaultCtaList(),
  screens: [],
});

export const createEmptyCarouselScreen = () => ({
  title: "",
  desc: "",
  assets: { ...EMPTY_ASSET },
  cta: createDefaultCtaList(),
});

const normalizeRoles = (userRoles = []) =>
  (Array.isArray(userRoles) ? userRoles : [])
    .filter(Boolean)
    .map((role) => String(role).toUpperCase());

const isL3OrHigher = (userRoles = []) => {
  const roles = normalizeRoles(userRoles);
  return (
    roles.includes(customConfig.roleCustomerSupportL3) ||
    roles.includes(customConfig.roleCustomerSupportL3Special) ||
    roles.includes(customConfig.roleCustomerSupportL4) ||
    roles.includes(customConfig.roleCloudAdmin)
  );
};

const isL3OrHigherByLevel = (userLevel = 0) => Number(userLevel) >= 3;

const hasElevatedSupportAccess = (userRoles = [], userLevel = 0) =>
  isL3OrHigher(userRoles) || isL3OrHigherByLevel(userLevel);

export const getInAppNotificationPermission = (
  userRoles = [],
  operation,
  userLevel = 0,
) => {
  const roles = normalizeRoles(userRoles);
  const roleBasedPermissions = {
    createBroadcast: hasElevatedSupportAccess(userRoles, userLevel),
    createUserSpecific: hasElevatedSupportAccess(userRoles, userLevel),
    editNotification: hasElevatedSupportAccess(userRoles, userLevel),
    deleteNotification: hasElevatedSupportAccess(userRoles, userLevel),
    configureUsers:
      hasElevatedSupportAccess(userRoles, userLevel) ||
      roles.includes(ROLE_SYSTEM_EWARRANTY_NOTIFICATION),
    configureMobileNumbers: roles.includes(ROLE_SYSTEM_EWARRANTY_NOTIFICATION),
    uploadUserIdsCsv: hasElevatedSupportAccess(userRoles, userLevel),
  };

  return Object.prototype.hasOwnProperty.call(roleBasedPermissions, operation)
    ? roleBasedPermissions[operation]
    : false;
};

export const IN_APP_NOTIFICATION_ALLOWED_HTML_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "mark",
  "del",
  "ins",
  "u",
  "small",
  "sup",
  "sub",
  "span",
  "br",
];

export const IN_APP_NOTIFICATION_ALLOWED_CONTENT =
  `${IN_APP_NOTIFICATION_ALLOWED_HTML_TAGS.filter((t) => t !== "br").join(" ")} span{color,background-color,font-size}; br`;
